/**
 * seo.js — Utilitaire de mise à jour dynamique des méta-données SEO par page.
 *
 * Met à jour en une seule fonction :
 *   - <title>
 *   - <meta name="description">
 *   - <link rel="canonical">
 *   - <meta property="og:*"> (Open Graph)
 *   - <meta name="twitter:*"> (Twitter Cards)
 */

const BASE_URL = 'https://www.superasso.fr';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;

/**
 * @param {Object} params
 * @param {string} params.title       - Titre de la page (sans suffix)
 * @param {string} params.description - Meta description (max ~155 car.)
 * @param {string} [params.path]      - Chemin relatif ex: "/contact"
 * @param {string} [params.image]     - URL absolue de l'image OG (optionnel)
 */
export function updateSEOMeta({ title, description, path = '/', image = DEFAULT_IMAGE }) {
  const fullTitle = title;
  const fullUrl = `${BASE_URL}${path}`;

  // ── Titre ──────────────────────────────────────────────────────
  document.title = fullTitle;

  // ── Meta description ───────────────────────────────────────────
  setMeta('name', 'description', description);

  // ── Canonical ──────────────────────────────────────────────────
  setLink('canonical', fullUrl);

  // ── Open Graph ─────────────────────────────────────────────────
  setMeta('property', 'og:title', fullTitle);
  setMeta('property', 'og:description', description);
  setMeta('property', 'og:url', fullUrl);
  setMeta('property', 'og:image', image);

  // ── Twitter / X ────────────────────────────────────────────────
  setMeta('name', 'twitter:title', fullTitle);
  setMeta('name', 'twitter:description', description);
  setMeta('name', 'twitter:image', image);
}

// ── Helpers ──────────────────────────────────────────────────────

function setMeta(attr, key, value) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

// ── Métadonnées par route ─────────────────────────────────────────

export const PAGE_SEO = {
  home: {
    title: "SuperAsso — Le logiciel de gestion d'association simple et efficace",
    description:
      "SuperAsso est la plateforme de gestion tout-en-un pour les associations loi 1901. Gérez vos membres, votre trésorerie, vos newsletters et vos statuts simplement. Logiciel gratuit pour les petites associations.",
    path: '/',
  },
  contact: {
    title: "À propos du projet — SuperAsso",
    description:
      "Derrière SuperAsso, il y a Sandro, président d'asso depuis 5 ans. Découvrez l'histoire du projet et contactez l'équipe à contact@superasso.fr.",
    path: '/contact',
  },
  docs: {
    title: "Documentation — SuperAsso",
    description:
      "Guides et tutoriels pour tirer le meilleur parti de SuperAsso : gestion des membres, comptabilité, newsletters, réunions de bureau et plus encore.",
    path: '/docs',
  },
  legal: {
    title: "Mentions Légales — SuperAsso",
    description:
      "Mentions légales du site superasso.fr : éditeur, hébergeur, propriété intellectuelle et responsabilité.",
    path: '/legal',
  },
  confidentialite: {
    title: "Politique de Confidentialité & RGPD — SuperAsso",
    description:
      "Comment SuperAsso protège vos données et celles de vos membres. Conformité RGPD, durée de conservation, droits d'accès et de suppression.",
    path: '/confidentialite',
  },
  cgu: {
    title: "CGU & CGV — SuperAsso",
    description:
      "Conditions Générales d'Utilisation et de Vente de SuperAsso : offres gratuite et premium, modalités de paiement, résiliation sans engagement.",
    path: '/cgu',
  },
  faq: {
    title: "FAQ : Tout savoir sur la gestion d'asso — SuperAsso",
    description:
      "Des questions sur la gestion de votre association loi 1901 ? Retrouvez toutes les réponses sur la trésorerie, les membres, et pourquoi choisir SuperAsso.",
    path: '/faq',
  },
};
